import type { PermissionState } from './hooks';
import type { SmartFeatureItem } from './tabs/TimeTrackingSmartFeaturesSection';
import type { AdminSubSettingItem } from './tabs/AdministrationSubSettings';

interface SmartFeatureGlobalSettings {
  enableCopyPreviousWeek: boolean;
  copyDescriptions: boolean;
  copyOnlyBillable: boolean;
  enableAutoSmartFill: boolean;
  enableAiInsights: boolean;
}

type SetPermission = <K extends keyof PermissionState>(key: K, value: PermissionState[K]) => void;

type SmartFeatureItemWithSetter = SmartFeatureItem & { setter: (value: boolean) => void };

export const buildSmartFeatureItems = (
  permissions: PermissionState,
  globalSettings: SmartFeatureGlobalSettings,
  updatePermission: SetPermission,
): SmartFeatureItemWithSetter[] => [
  {
    id: 'copy-last-week',
    label: "Enable 'Copy from Last Week'",
    description: 'Allow users to copy entries from previous week',
    enabled: globalSettings.enableCopyPreviousWeek,
    value: permissions.enableCopyFromLastWeek,
    setter: (value: boolean) => updatePermission('enableCopyFromLastWeek', value),
  },
  {
    id: 'copy-description',
    label: 'Copy Description',
    description: 'Include entry descriptions when copying',
    enabled: globalSettings.copyDescriptions,
    value: permissions.enableCopyDescription,
    setter: (value: boolean) => updatePermission('enableCopyDescription', value),
  },
  {
    id: 'copy-billable',
    label: 'Copy only Billable Entries',
    description: 'When copying, exclude non-billable time',
    enabled: globalSettings.copyOnlyBillable,
    value: permissions.enableCopyOnlyBillable,
    setter: (value: boolean) => updatePermission('enableCopyOnlyBillable', value),
  },
  {
    id: 'auto-smart-fill',
    label: 'Enable Auto Smart Fill',
    description: 'Automatically suggest time entries based on patterns',
    enabled: globalSettings.enableAutoSmartFill,
    value: permissions.enableAutoSmartFill,
    setter: (value: boolean) => updatePermission('enableAutoSmartFill', value),
  },
  {
    id: 'ai-insights',
    label: 'Enable AI Insights',
    description: 'Show AI-powered insights and anomaly detection',
    enabled: globalSettings.enableAiInsights,
    value: permissions.enableAiInsights,
    setter: (value: boolean) => updatePermission('enableAiInsights', value),
  },
];

export const buildAdminSubSettings = (
  permissions: PermissionState,
  updatePermission: SetPermission,
): AdminSubSettingItem[] => [
  {
    id: 'manage-deadlines',
    label: 'Manage Deadlines',
    description: 'Define and adjust timesheet submission cutoff dates, approval deadlines, and period-end schedules',
    value: permissions.manageDeadlines,
    setter: (value: boolean) => updatePermission('manageDeadlines', value),
  },
  {
    id: 'manage-policy-settings',
    label: 'Manage Policy Settings',
    description: 'Configure automated rules for detecting and flagging policy violations, including overtime limits and break requirements',
    value: permissions.managePolicySettings,
    setter: (value: boolean) => updatePermission('managePolicySettings', value),
  },
  {
    id: 'manage-utilisation-settings',
    label: 'Manage Utilisation Settings',
    description: 'Set target utilization rates, billability thresholds, and capacity planning parameters for resources',
    value: permissions.manageUtilisationSettings,
    setter: (value: boolean) => updatePermission('manageUtilisationSettings', value),
  },
  {
    id: 'manage-time-entry-settings',
    label: 'Manage Time Entry Settings',
    description: 'Configure time entry rules, validation settings, and entry submission requirements',
    value: permissions.manageTimeEntrySettings,
    setter: (value: boolean) => updatePermission('manageTimeEntrySettings', value),
  },
  {
    id: 'manage-work-type-settings',
    label: 'Manage Work Type Settings',
    description: 'Define and manage work types, overtime classifications, and billing rate multipliers',
    value: permissions.manageWorkTypeSettings,
    setter: (value: boolean) => updatePermission('manageWorkTypeSettings', value),
  },
  {
    id: 'manage-approvals',
    label: 'Manage Approvals',
    description: 'Design and modify approval chains, set approval hierarchies, and configure escalation rules for timesheets and requests',
    value: permissions.manageApprovalsSettings,
    setter: (value: boolean) => updatePermission('manageApprovalsSettings', value),
  },
  {
    id: 'manage-notifications',
    label: 'Manage Notifications',
    description: 'Control when and how users receive email, in-app, and push notifications for various system events and reminders',
    value: permissions.manageNotifications,
    setter: (value: boolean) => updatePermission('manageNotifications', value),
  },
];
